import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-white font-body">
      <nav className="sticky top-0 z-50 bg-[#090D17] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="h-16 flex items-center justify-between">
            <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate('/')}>
              <img src="https://customer-assets-jai6qajn.emergentagent.net/job_38d96a59-41a3-4967-8296-b7ba8cd6e1a1/artifacts/rrpmz7yf_logo%20%287%29.png" alt="OwnerSide" className="h-7 w-7 object-contain" />
              <span className="font-heading text-lg font-semibold tracking-tight text-white">OwnerSide</span>
            </div>
            <button onClick={() => navigate('/')} className="flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors">
              <ArrowLeft className="h-3.5 w-3.5" /> Home
            </button>
          </div>
        </div>
      </nav>
      <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16 py-16 sm:py-20">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-950 mb-3">Privacy Policy</h1>
        <p className="text-sm text-slate-400 mb-12">Last updated: August 18, 2026</p>

        <div className="prose-custom space-y-10 text-slate-600 text-[15px] leading-relaxed">
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-950 mb-3">1. Introduction</h2>
            <p>OwnerSide ("we," "our," or "us"), operated by OwnerSide Business Services Ltd., is a business advisory and executive support company. We are committed to protecting the privacy and security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard information when you visit our website at owner-side.com or contact us about our services (collectively, the "Service").</p>
            <p className="mt-3">This Privacy Policy explains how we collect, use, disclose, and safeguard personal information when you visit our website or contact us about our services.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-slate-950 mb-3">2. Information We Collect</h2>
            <h3 className="font-heading text-base font-semibold text-slate-800 mt-5 mb-2">2.1 Information You Provide</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Contact Information:</strong> Your name, email address, and phone number, if provided.</li>
              <li><strong>Business Information:</strong> Company or business details, if provided.</li>
              <li><strong>Consultation Requests:</strong> The content of any message you submit describing your business needs, questions, or the support you are looking for through our consultation request form.</li>
            </ul>
            <h3 className="font-heading text-base font-semibold text-slate-800 mt-5 mb-2">2.2 Information Collected Automatically</h3>
            <p>When you visit our website, we may automatically collect ordinary technical and usage information, such as your browser type, device type, and the pages you visit, to help us maintain and operate the website. See our <Link to="/cookies" className="text-[#0E7C72] hover:underline">Advertising & Cookies Policy</Link> for details on what, if anything, is collected in this way.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-slate-950 mb-3">3. How We Use Your Information</h2>
            <p>We use the information we collect for the following purposes:</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li><strong>Responding to Inquiries:</strong> To respond to your consultation requests and answer your questions.</li>
              <li><strong>Providing Services:</strong> To deliver the business advisory and executive support services you engage us for.</li>
              <li><strong>Business Communications:</strong> To communicate with you about your engagement or our services.</li>
              <li><strong>Website Operation and Security:</strong> To operate, maintain, and secure our website.</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal obligations.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-slate-950 mb-3">4. Data Sharing and Disclosure</h2>
            <p>We do not sell your personal information. We may share your information in the following limited circumstances:</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li><strong>Service Providers:</strong> With trusted third-party providers who help us operate our website or communicate with you (for example, hosting or email delivery providers), under appropriate confidentiality obligations.</li>
              <li><strong>Legal Requirements:</strong> When required by law, subpoena, court order, or government request.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-slate-950 mb-3">5. Data Retention</h2>
            <p>We retain personal information only for as long as necessary to respond to your inquiry, provide our services, and meet our legitimate business record-keeping and legal obligations. You may request deletion of your information at any time, as described in Section 6 below.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-slate-950 mb-3">6. Your Rights</h2>
            <p>You may have the following rights regarding your personal information, depending on your jurisdiction:</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you.</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information.</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information.</li>
              <li><strong>Withdrawal of Consent:</strong> Withdraw your consent at any time where our processing is based on consent.</li>
            </ul>
            <p className="mt-3">To exercise any of these rights, please contact us at info@owner-side.com.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-slate-950 mb-3">7. Data Security</h2>
            <p>We take reasonable technical and organizational measures to help protect the personal information we hold from unauthorized access, use, or disclosure. However, no method of electronic transmission or storage is completely secure, and we cannot guarantee absolute security.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-slate-950 mb-3">8. Where Your Information Is Processed</h2>
            <p>OwnerSide Business Services Ltd. operates from Canada. Your information may be stored or processed by service providers located in Canada or elsewhere. We take reasonable steps to ensure that any such processing is consistent with this Privacy Policy.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-slate-950 mb-3">9. Children's Privacy</h2>
            <p>Our Service is intended for business owners and is not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a minor, please contact us so we can remove it.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-slate-950 mb-3">10. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on our website with a revised "Last updated" date. Your continued use of the Service after changes are posted constitutes your acceptance of the revised policy.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-slate-950 mb-3">11. Contact Us</h2>
            <p>If you have any questions about this Privacy Policy or our data practices, please contact us:</p>
            <div className="mt-3 bg-slate-50 rounded-xl p-5 text-sm space-y-1">
              <p><strong>OwnerSide Business Services Ltd.</strong></p>
              <p>Email: info@owner-side.com</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
